import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import ReactGA4 from 'react-ga4';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import * as Sentry from '@sentry/browser';
import produce from 'immer';
import { getAccount } from '@wagmi/core';
import message from 'antd/es/message';

import { IconLoginTick, IconLogOut, IconWarningMark } from '@/assets/login';
import { IconRcHi, IconStream } from 'assets/sidebar';
import { Button, UserAvatar, Modal, Tips } from '@/components';
import { deleteRefcode } from '@/components/ReferralCodeBtn/hooks';
import {
  useCurrentAddressInfo,
  useLoginedAddresses,
  useQueryTotalBalance,
  useConnectedAddress,
  useUserInfo,
  useSwitch,
  logoutUser,
  useUpdateCurrentAddressInfo,
  useUserDetail,
  useCurrentAddressAccount,
} from '@/hooks';
import {
  shorten,
  ProfileUSDValue,
  setCurrentAddress,
  logout,
  deleteAddress,
  login,
  getCredential,
} from '@/utils';

import { AppLoginQrcode } from '../AppLoginQrcode';
import { useAccountsNotification } from '../../hooks/account';
import style from './index.module.scss';

const { CommonModal } = Modal;

type Props = { onClose: () => void; on: boolean };

export const AccountSwitcherModal = ({ onClose, on }: Props) => {
  const { t } = useTranslation();
  const { address } = useCurrentAddressInfo();
  const { data: userInfo } = useUserDetail(address);
  const netAssets = useQueryTotalBalance(address);
  const loginedList = useLoginedAddresses();
  const connectedAddress = useConnectedAddress();
  const update = useUpdateCurrentAddressInfo();
  const { data: currentL2Account } = useCurrentAddressAccount();

  const { data: connectedUser, isFetched } = useUserInfo(connectedAddress);
  const notiDict = useAccountsNotification();

  const { on: isManage, toggle: toggleManage, turnOff } = useSwitch();

  const gaActionRef = useRef('');

  const walletName = useMemo(() => getAccount().connector?.name || 'Wallet', []);

  const [currentUser, accounts] = useMemo(() => {
    const accounts = loginedList
      .filter(x => !!x.userInfo)
      .map(x => ({
        ...x,
        userId: x.userInfo?.desc?.user?.web3_id || shorten(x.id, 6, 4),
        netWorth: ProfileUSDValue(
          (x.id === address && netAssets?.id?.toLowerCase() === address
            ? netAssets.total_usd_value
            : x.userInfo?.desc?.usd_value) || 0,
        ),
      }));

    const currentIdx = accounts.findIndex(x => x.id === address);
    const currentUser = accounts[currentIdx];
    accounts.splice(currentIdx, 1);

    return [
      currentUser,
      accounts.sort(
        (m, n) => (n.userInfo?.desc?.usd_value || 0) - (m.userInfo?.desc?.usd_value || 0),
      ),
    ];
  }, [loginedList, netAssets, address]);

  useEffect(() => {
    if (currentL2Account && address && currentL2Account.id === address) {
      update(pre => ({
        ...pre,
        l2Balance: currentL2Account.balance,
      }));
    }
  }, [currentL2Account, address, update]);

  useEffect(() => {
    if (userInfo && address && userInfo.id === address) {
      update(pre => {
        return produce(pre, next => {
          if (!next.userInfo) {
            next.userInfo = { ...userInfo };
          }

          if (netAssets && netAssets.id === address && pre.userInfo?.id === address) {
            if (next.userInfo?.desc) {
              next.userInfo.desc = { ...userInfo.desc! };
              next.userInfo.desc.usd_value = netAssets.total_usd_value;
            }
          }

          return next;
        });
      });
    }
  }, [userInfo, update, address, netAssets]);

  const handleAccountClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const { id } = e.currentTarget.dataset;
      if (id && id !== address) {
        setCurrentAddress(id.toLowerCase());
        message.info(t('AccountSwitcher_AccountSwitcherModal.switchSuccess'));
        ReactGA4.event({
          category: 'Switch Account',
          action: 'Switch Account',
        });
      }
      onClose();
    },
    [address, onClose, t],
  );

  const handleLogoutClick = useCallback(
    async (e: React.MouseEvent<SVGSVGElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isManage) {
        return;
      }

      const targetAddress = e.currentTarget.dataset.id;

      if (!targetAddress) {
        return;
      }
      ReactGA4.event({
        category: 'Click Account Icon',
        action: 'Account_Click_Log out',
      });

      await Modal.confirm({
        title: (
          <div className={style.confirmModalTitle}>
            <IconWarningMark />
            <div>{t('AccountSwitcher_AccountSwitcherModal.kmdb7e')}</div>
          </div>
        ),
        content: (
          <div className={style.confirmModalContent}>
            {t('AccountSwitcher_AccountSwitcherModal.1qrga3p')}
          </div>
        ),
        btnClassName: style.confirmModalBtn,
        cancelBtnType: 'default',
      });

      ReactGA4.event({
        category: 'Click Account Icon',
        action: 'Account_Confirm_Log out',
      });

      // 不阻塞下面流程
      logoutUser(getCredential(targetAddress)).catch(error => Sentry.captureException(error));

      if (targetAddress === address) {
        logout();
        onClose();
      } else {
        deleteAddress(targetAddress);
        deleteRefcode(targetAddress);
      }
    },
    [isManage, address, onClose, t],
  );

  const onLoginClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      login(true);
      onClose();
    },
    [onClose],
  );

  const handleClickManage = useCallback(() => {
    toggleManage();
    if (!isManage) {
      ReactGA4.event({
        category: 'Click Account Icon',
        action: 'Account_Click_Manage',
      });
    }
  }, [isManage, toggleManage]);

  useEffect(() => {
    if (!on) {
      turnOff();
      gaActionRef.current = '';
    }
  }, [on, turnOff]);

  useEffect(() => {
    if (!on || (connectedAddress && !isFetched)) return;
    const getAction = () => {
      if (connectedUser && !currentUser) {
        return 'Account_Only_Current';
      }
      if (currentUser) {
        const canSwitch = accounts?.length;
        if (!connectedUser) {
          return `Account_Logged${canSwitch ? '_Switch' : ''}_Current_Empty`;
        }
        if (currentUser.id === connectedUser?.id) {
          return `Account_Logged${canSwitch ? '_Switch' : ''}_Current_Same`;
        } else {
          return `Account_Logged${canSwitch ? '_Switch' : ''}_Current_Differ`;
        }
      }
    };
    const action = getAction();
    if (action && action !== gaActionRef.current) {
      ReactGA4.event({
        category: 'Account Status',
        action,
      });
      gaActionRef.current = action;
    }
  }, [accounts?.length, connectedAddress, connectedUser, currentUser, isFetched, on]);

  return (
    <CommonModal
      size="medium"
      onRequestClose={onClose}
      on={on}
      containerClassName={style.container}>
      <div className={style.header}>
        <div className={style.title}>{t('AccountSwitcher_AccountSwitcherModal.8259n1')}</div>
        <div className={style.ops}>
          {address ? (
            <div className={style.appLoginIcon}>
              <AppLoginQrcode />
            </div>
          ) : null}
          {currentUser ? (
            <Button className={style.titleBtn} type="text" onClick={handleClickManage}>
              {isManage
                ? t('UidSetting_MintIdModal.x9d2fu')
                : t('AccountSwitcher_AccountSwitcherModal.manage')}
            </Button>
          ) : null}
        </div>
      </div>
      {currentUser ? (
        <>
          <div className={style.sectionTitle}>
            {t('AccountSwitcher_AccountSwitcherModal.4ug5rq')}
          </div>
          <div className={cx([style.userItem, style.selected])}>
            <UserAvatar className={style.userAvatar} data={currentUser?.userInfo?.desc} size={48} />
            <div className={style.userInfo}>
              <div className={style.userId}>{currentUser.userId}</div>
              <div className={style.userAsset}>
                <span className={style.userAssetTitle}>
                  {t('UserInfoBtn_LoginedUserList.q6o2ej')}
                </span>
                <span className={style.userAssetDesc}>{currentUser.netWorth}</span>
              </div>
              <div className={style.userAsset}>
                <span className={style.userAssetTitle}>
                  {t('UserInfoBtn_LoginedUserList.rhvciq')}
                </span>
                <span className={style.userAssetDesc}>
                  {ProfileUSDValue(currentUser.l2Balance || 0, 2)}
                </span>
              </div>
            </div>
            {isManage ? (
              <IconLogOut
                className={style.logout}
                data-id={currentUser.id}
                onClick={handleLogoutClick}
              />
            ) : null}
            <IconLoginTick className={style.loginTick} />
          </div>
        </>
      ) : null}
      {accounts?.length ? (
        <>
          <div className={style.sectionTitle}>
            {t('AccountSwitcher_AccountSwitcherModal.4hxv8v')}
          </div>
          <div className={style.accountsList}>
            {accounts.map((user, i) => (
              <div
                key={user.id}
                data-id={user.id}
                className={cx([style.userItem, style.otherAccount, !isManage && style.selectable])}
                onClick={handleAccountClick}>
                <UserAvatar className={style.userAvatar} data={user?.userInfo?.desc} size={48} />
                <div className={style.userInfo}>
                  <div className={style.userId}>{user.userId}</div>
                  <div className={style.userAsset}>
                    <span className={style.userAssetTitle}>
                      {t('UserInfoBtn_LoginedUserList.q6o2ej')}
                    </span>
                    <span className={style.userAssetDesc}>{user.netWorth}</span>
                  </div>
                  <div className={style.notification}>
                    <NavLink to="/hi" className={style.notiTag}>
                      <IconRcHi className={style.notiIcon} />
                      {notiDict.hi[user.id] ? <div className={style.notiDot} /> : null}
                      Hi
                    </NavLink>
                    <NavLink to="/stream" className={style.notiTag}>
                      <IconStream className={style.notiIcon} />
                      {notiDict.stream[user.id] ? <div className={style.notiDot} /> : null}
                      {t('AccountSwitcher_AccountSwitcherModal.154lpcw')}
                    </NavLink>
                  </div>
                </div>
                {isManage ? (
                  <IconLogOut
                    className={style.logout}
                    data-id={user.id}
                    onClick={handleLogoutClick}
                  />
                ) : null}
                <IconLoginTick className={style.loginTick} />
              </div>
            ))}
          </div>
        </>
      ) : null}
      <div className={style.sectionTitle}>
        {t('AccountSwitcher_AccountSwitcherModal.1jkom96', { wallet: walletName })}
        <span className={style.howSwitch}>{t('AccountSwitcher_AccountSwitcherModal.1kjf72h')}</span>
        <Tips title={t('AccountSwitcher_AccountSwitcherModal.1ro84xq')} ghost />
      </div>
      {connectedAddress ? (
        <NavLink to={`/profile/${connectedAddress}`} onClick={onClose}>
          <div className={style.userItem}>
            <UserAvatar className={style.userAvatar} data={connectedUser?.desc} size={48} />
            <div className={style.userInfo}>
              <div className={style.userId}>
                {connectedUser?.desc?.user?.web3_id || shorten(connectedAddress, 6, 4)}
              </div>
              <div className={style.userAddr}>{connectedAddress}</div>
            </div>
            {connectedAddress === address ? (
              <span className={style.logged}>
                {t('AccountSwitcher_AccountSwitcherModal.3mp55v')}
              </span>
            ) : (
              <Button className={style.loginBtn} type="primary" ghost onClick={onLoginClick}>
                {t('AccountSwitcher_AccountSwitcherModal.17tfm1')}
              </Button>
            )}
          </div>
        </NavLink>
      ) : (
        <div
          onClick={onLoginClick}
          className={cx(style.userItem, style.selectable, style.loginOther)}>
          {t('UserInfoBtn_LoginedUserList.mnfax')}
        </div>
      )}
    </CommonModal>
  );
};
